import {
  Box,
  Flex,
  Portal,
  SlideFade,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { RiIndentDecrease } from "@remixicon/react";
import { useState } from "react";
import { useDragLayer, useDrop } from "react-dnd";
import { useParams } from "react-router";

import { ErrorResponseDisplay } from "@/common/components/ErrorResponseDisplay";
import { DragType } from "@/common/types/dragDropTypes";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { FeatureName } from "@reflag/shared/featureAPI";

export function OrphanFeatureDropTarget() {
  const toast = useToast();
  const { companyId } = useParams();

  const featureUpdateMutation = useFeatureUpdateMutation(companyId!);

  const [isDropping, setIsDropping] = useState(false);

  const { item, itemType, isDragging } = useDragLayer((monitor) => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    isDragging: monitor.isDragging(),
  }));

  const shouldShow =
    isDropping ||
    (isDragging &&
      itemType === DragType.FeatureDisplay &&
      item.parentFeatureId !== null);

  const [{ isOver }, drop] = useDrop<FeatureName, void, { isOver: boolean }>({
    accept: DragType.FeatureDisplay,
    collect(monitor) {
      return {
        isOver: monitor.isOver(),
      };
    },
    drop(feature) {
      setIsDropping(true);
      featureUpdateMutation.mutate(
        {
          id: feature.id,
          parentFeatureId: null,
        },
        {
          onSuccess() {
            toast({
              title: `Parent removed from "${feature.name}"`,
              status: "success",
            });
          },
          onError(error) {
            toast({
              title: (
                <Box>
                  <Text>{`Failed moving "${feature.name}".`}</Text>
                  <ErrorResponseDisplay response={error.response?.data} />
                </Box>
              ),
              status: "error",
              duration: 5000,
            });
          },
          onSettled() {
            setIsDropping(false);
          },
        },
      );
    },
  });

  const bgColor = useColorModeValue("gray.950", "gray.50");
  const hoverBgColor = useColorModeValue("gray.600", "gray.300");
  const textColor = useColorModeValue("white", "gray.950");

  return (
    <Portal>
      <SlideFade in={shouldShow} unmountOnExit={true}>
        <Flex
          ref={drop}
          alignItems="center"
          bgColor={isOver ? hoverBgColor : bgColor}
          borderWidth="0.5px"
          bottom={6}
          boxShadow="md"
          color={textColor}
          gap={2}
          left="50%"
          position="fixed"
          px={6}
          py={4}
          rounded="full"
        >
          <RiIndentDecrease size={16} />
          Drop here to move the flag to top level
        </Flex>
      </SlideFade>
    </Portal>
  );
}
